import { useEffect, useState } from 'preact/hooks';
import type { PrDetailBody } from '../../shared/pr/api';
import { similarPrices, summarise, type PriceHistoryBody } from '../../shared/prices/history';
import { normaliseItem } from '../../shared/quotes/rules';
import { useI18n } from '../i18n';
import { apiGet } from '../lib/api';
import { formatDate } from '../lib/dates';

// Past prices for each line of the request (D85), for Procurement on the request page: the last
// purchase (when, from whom, unit price, PO) and the range, from the same price history as the
// Price history page (D72). One read: the request's category.

const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );

type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'ok'; data: PriceHistoryBody };

export function LinePrices({ pr }: { pr: PrDetailBody }) {
  const { t, lang } = useI18n();
  const category = pr.category?.id ?? '';
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    if (!category) return;
    let live = true;
    apiGet<PriceHistoryBody>(`/api/prices?category=${encodeURIComponent(category)}`)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch(() => live && setLoad({ kind: 'error' }));
    return () => {
      live = false;
    };
  }, [category]);

  if (!category || pr.lines.length === 0) return null;

  return (
    <section class="card card--insight stack" aria-labelledby="d-prices">
      <div class="card__head">
        <h3 id="d-prices" class="card__title">
          {t('lp.title')}
        </h3>
        <a href="#/prices">{t('lp.all')}</a>
      </div>
      {load.kind === 'loading' && <p aria-busy="true">{t('req.loading')}</p>}
      {load.kind === 'error' && <p role="alert">{t('lp.error')}</p>}
      {load.kind === 'ok' &&
        pr.lines.map((l) => {
          const rows = similarPrices(
            load.data.rows.filter((r) => r.prNo !== pr.prNo),
            l.description,
            normaliseItem,
          );
          const sum = summarise(rows);
          const last = sum.last;
          return (
            <div key={l.lineNo} class="lp-line">
              <p class="lp-line__item" dir="auto">
                <span class="ref" dir="ltr">
                  {String(l.lineNo).padStart(2, '0')}
                </span>{' '}
                {l.description}
              </p>
              {!last ? (
                <p class="caption">{t('lp.none')}</p>
              ) : (
                <>
                  <dl class="facts facts--compact">
                    <div>
                      <dt>{t('lp.lastBought')}</dt>
                      <dd>{last.date ? formatDate(last.date, lang) : '—'}</dd>
                    </div>
                    <div>
                      <dt>{t('lp.lastPrice')}</dt>
                      <dd dir="ltr">
                        {last.currency} {money(last.unitPrice)}
                      </dd>
                    </div>
                    <div>
                      <dt>{t('lp.supplier')}</dt>
                      <dd dir="auto">{last.supplierName || '—'}</dd>
                    </div>
                    <div>
                      <dt>{t('lp.range', { n: String(sum.count) })}</dt>
                      <dd dir="ltr">
                        {sum.minSAR === null || sum.maxSAR === null
                          ? '—'
                          : `SAR ${money(sum.minSAR)} – ${money(sum.maxSAR)}`}
                      </dd>
                    </div>
                  </dl>
                  {/* Scrolls sideways on a phone, so it takes keyboard focus (WCAG 2.1.1). */}
                  <div
                    class="table-wrap"
                    // A scrollable region must be reachable by keyboard (axe scrollable-region-focusable).
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                    tabIndex={0}
                    role="region"
                    aria-label={t('lp.tableLabel', { line: String(l.lineNo).padStart(2, '0') })}
                  >
                    <table class="table">
                      <thead>
                        <tr>
                          <th scope="col">{t('lp.col.date')}</th>
                          <th scope="col">{t('lp.col.item')}</th>
                          <th scope="col">{t('lp.supplier')}</th>
                          <th scope="col" class="num">
                            {t('lp.col.qty')}
                          </th>
                          <th scope="col" class="num">
                            {t('lp.col.unit')}
                          </th>
                          <th scope="col">{t('lp.col.ref')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, i) => (
                          <tr key={`${r.poNo}-${i}`}>
                            <td class="nowrap">{r.date ? formatDate(r.date, lang) : '—'}</td>
                            <td dir="auto">{r.item}</td>
                            <td dir="auto">{r.supplierName || '—'}</td>
                            <td class="num" dir="ltr">
                              {r.qty}
                            </td>
                            <td class="num nowrap" dir="ltr">
                              {r.currency} {money(r.unitPrice)}
                            </td>
                            <td class="ref" dir="ltr">
                              {r.poNo || r.prNo || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          );
        })}
    </section>
  );
}
